import React from 'react';

export interface EmptyStateIconProps {
  /** Icon width */
  width?: number;
  /** Icon height */
  height?: number;
  /** Additional CSS classes */
  className?: string;
}

// Default empty state icon with list and cursor
export const EmptyStateIcon: React.FC<EmptyStateIconProps> = ({
  width = 161,
  height = 120,
  className = '',
}) => {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 161 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      {/* List items */}
      <rect x="20" y="20" width="90" height="12" rx="6" fill="var(--color-primary-base)" />
      <rect x="20" y="50" width="60" height="12" rx="6" fill="var(--color-primary-base)" />

      {/* Cursor/Pointer */}
      <g transform="translate(80, 45)">
        <path
          d="M0 0L0 20L7 14L12 22L16 20L11 12L20 12L0 0Z"
          fill="var(--color-text-primary)"
          stroke="var(--color-neutrals-white)"
          strokeWidth="1.5"
        />
      </g>
    </svg>
  );
};

// Search list icon for empty states
export const SearchListIcon: React.FC<EmptyStateIconProps> = ({
  width = 161,
  height = 120,
  className = '',
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 161 120"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <rect
      x="20"
      y="10"
      width="120"
      height="100"
      rx="8"
      fill="var(--color-grey-light, #F7F7F7)"
      stroke="var(--color-grey-neutral, #D4D4D4)"
      strokeWidth="2"
    />
    <rect x="35" y="30" width="90" height="12" rx="4" fill="var(--color-grey-soft, #EAEAEA)" />
    <rect x="35" y="50" width="70" height="12" rx="4" fill="var(--color-grey-soft, #EAEAEA)" />
    <rect x="35" y="70" width="80" height="12" rx="4" fill="var(--color-grey-soft, #EAEAEA)" />
    <circle
      cx="130"
      cy="90"
      r="25"
      fill="var(--color-surface-primary, #FFFFFF)"
      stroke="var(--color-primary-base, #5F5BF3)"
      strokeWidth="3"
    />
    <path
      d="M130 80V100M120 90H140"
      stroke="var(--color-primary-base, #5F5BF3)"
      strokeWidth="3"
      strokeLinecap="round"
    />
  </svg>
);

// Select from list icon
export const SelectFromListIcon: React.FC<EmptyStateIconProps> = ({
  width = 161,
  height = 120,
  className = '',
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 161 120"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <rect
      x="20"
      y="10"
      width="120"
      height="100"
      rx="8"
      fill="var(--color-grey-light, #F7F7F7)"
      stroke="var(--color-grey-neutral, #D4D4D4)"
      strokeWidth="2"
    />
    <rect
      x="35"
      y="30"
      width="90"
      height="16"
      rx="4"
      fill="var(--color-primary-base, #5F5BF3)"
      fillOpacity="0.1"
      stroke="var(--color-primary-base, #5F5BF3)"
      strokeWidth="2"
    />
    <rect x="35" y="54" width="90" height="16" rx="4" fill="var(--color-grey-soft, #EAEAEA)" />
    <rect x="35" y="78" width="90" height="16" rx="4" fill="var(--color-grey-soft, #EAEAEA)" />
    <path
      d="M145 35L155 45L145 55"
      stroke="var(--color-primary-base, #5F5BF3)"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Magic search icon (for AI lookup)
export const MagicSearchIcon: React.FC<EmptyStateIconProps> = ({
  width = 80,
  height = 80,
  className = '',
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 80 80"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <defs>
      <linearGradient id="magicGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="var(--color-primary-base, #5F5BF3)" />
        <stop offset="100%" stopColor="var(--color-secondary-base, #B3425A)" />
      </linearGradient>
    </defs>
    <circle cx="35" cy="35" r="20" stroke="url(#magicGradient)" strokeWidth="4" fill="none" />
    <path d="M50 50L65 65" stroke="url(#magicGradient)" strokeWidth="4" strokeLinecap="round" />
    <path
      d="M60 15L62 20L67 22L62 24L60 29L58 24L53 22L58 20L60 15Z"
      fill="var(--color-primary-base, #5F5BF3)"
    />
    <path
      d="M70 35L71 38L74 39L71 40L70 43L69 40L66 39L69 38L70 35Z"
      fill="var(--color-secondary-base, #B3425A)"
    />
  </svg>
);

// Database check icon for "type already exists" state
export const DatabaseCheckIcon: React.FC<EmptyStateIconProps> = ({
  width = 82,
  height = 82,
  className = '',
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.0267 22.5333C12.1692 20.5377 12.8361 18.6151 13.96 16.96C11.8667 17.3067 9.78667 17.3333 9.33333 17.3333C8.37333 17.3333 0 17.24 0 14V19.3333C0 22.5733 8.37333 22.6667 9.33333 22.6667C9.64 22.6667 10.72 22.6533 12.0267 22.5333ZM9.33333 14.6667C10.288 14.6667 18.6667 14.5733 18.6667 11.3333V6C18.6667 9.24 10.288 9.33333 9.33333 9.33333C8.37867 9.33333 0 9.24 0 6V11.3333C0 14.5733 8.37867 14.6667 9.33333 14.6667Z"
      fill="var(--color-primary-light, #B7A8F7)"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M23.3333 14.6667C18.5547 14.6667 14.6667 18.5547 14.6667 23.3333C14.6667 28.112 18.5547 32 23.3333 32C28.112 32 32 28.112 32 23.3333C32 18.5547 28.112 14.6667 23.3333 14.6667ZM23.0027 26.5453L27.6693 21.212C27.8866 20.9438 27.9916 20.6019 27.9622 20.2581C27.9328 19.9142 27.7714 19.595 27.5117 19.3676C27.2521 19.1403 26.9144 19.0223 26.5696 19.0385C26.2249 19.0547 25.8998 19.2039 25.6627 19.4547L21.9347 23.716L20.276 22.0573C20.1522 21.9335 20.0052 21.8353 19.8435 21.7683C19.6818 21.7014 19.5084 21.6669 19.3333 21.6669C19.1583 21.6669 18.9849 21.7014 18.8232 21.7683C18.6614 21.8353 18.5145 21.9335 18.3907 22.0573C18.2669 22.1811 18.1687 22.3281 18.1017 22.4898C18.0347 22.6516 18.0002 22.8249 18.0002 23C18.0002 23.1751 18.0347 23.3484 18.1017 23.5102C18.1687 23.6719 18.2669 23.8189 18.3907 23.9427L21.0573 26.6093C21.3073 26.8594 21.6464 26.9999 22 27H22.044C22.2264 26.9939 22.4056 26.9505 22.5706 26.8722C22.7355 26.794 22.8826 26.6828 23.0027 26.5453Z"
      fill="#10B981"
    />
    <path
      d="M9.33333 6.66667C8.37867 6.66667 0 6.57333 0 3.33333C0 0.0933332 8.37867 0 9.33333 0C10.288 0 18.6667 0.0933332 18.6667 3.33333C18.6667 6.57333 10.288 6.66667 9.33333 6.66667Z"
      fill="var(--color-primary-light, #B7A8F7)"
    />
  </svg>
);

export default EmptyStateIcon;
